/* ---------------------- Raw API Shapes ---------------------- */
// Exact shapes returned by /api/missionManagerDashboard/getDashboardData

export interface RawAddress {
  _id?: string;
  addressLineOne?: string;
  addressLineTwo?: string;
  cityOrTown?: string;
  governingDistrict?: string;
  zipCode?: string;
  country?: string;
}

export interface RawMissionManagerUrl {
  _id?: string | null;
  name?: string | null;
  domain?: string | null;
}

export interface RawCurrentLocation {
  _id?: string;
  address?: RawAddress;
  missionManagerUrls?: RawMissionManagerUrl[];
}

export interface RawLocationOverride {
  lat?: number | null;
  lon?: number | null;
  alt?: number | null;
  heading?: number | null;
}

export interface RawDrone {
  _id?: string | null;
  name?: string | null;
  status?: string | null;
  model?: string | null;
  faaRegistrationNumber?: string | null;
  remoteIDnumber?: string | null;
  totalFlightTime?: number | null; // milliseconds
  totalFlightMinutes?: number | null;
  firstActivated?: string | null;
  stopReportToFmForMoreThan30Mins?: boolean | null;
}

export interface RawBase {
  _id?: string | null;
  name?: string | null;
  status?: string | null;
  currentLocation?: RawCurrentLocation;
  drones?: RawDrone[];
  totalMissions?: number | null;
  successfulMissions?: number | null;
  longestMissionDuration?: number | null; // milliseconds
  shortestMissionDuration?: number | null; // milliseconds
  averageMissionDuration?: number | null; // milliseconds
  lastMissionLandingDate?: string | null;
  totalNightMissions?: number | null;
  totalFlightTimeBase?: number | null; // milliseconds

  totalNumberOfRaidEvents?: number | null;
  weeklyScheduledMissions?: number | null;

  lat?: number | null;
  lon?: number | null;
  alt?: number | null;
  locationOverride?: RawLocationOverride | null;
  location_override?: RawLocationOverride | null; // some APIs return this snake_case field instead of locationOverride
  stopReportToFmForMoreThan30Mins?: boolean | null;
}

export interface RawApiResponse {
  bases?: RawBase[];
  startDate?: string | null;
  endDate?: string | null;
}

/* ---------------------- Raw Websocket Shapes ---------------------- */

export interface RawDroneWsMessage {
  name?: string;
  base_name?: string;
  grounded?: boolean | null;
  battery_charge_state?: number | null; // 0–1 float
  battery?: number | null;
  bms_current?: number | null;
  bms_cycle_count?: number | null;
  battery_raw_current?: number | null;
  lat?: number | null;
  lon?: number | null;
  base_id?: number;
}

export interface RawBaseWsData {
  name?: string;
  status?: string | null;
  location_override?: RawLocationOverride | null;
}

export interface RawBaseWsMessage {
  channel?: 'base';
  data?: RawBaseWsData;
}

/* ---------------------- System Status Enum + Classification Sets ---------------------- */

export enum SystemStatus {
  OPERATIONAL = 'Operational',
  MAINTENANCE = 'Maintenance',
  GROUNDED = 'Grounded',
  OFFLINE = 'Offline',
  PLANNED = 'Planned',
}

/** Base string statuses that count as "operational" for SystemStatus derivation. */
export const OPERATIONAL_BASE_STATUSES = new Set([
  'Deployed Operational',
  'Deployed Backup',
  'Deployment Customer Validation',
  'Training and Handoff to Customer',
  'Customer Operation',
  'Periodic System Maintenance / On-Site Inspection',
  'On-site Validation',
]);

/** Base string statuses that count as "maintenance" for SystemStatus derivation. */
export const MAINTENANCE_BASE_STATUSES = new Set([
  'No Status',
  'VP (Validation Provisioning)',
  'Validation',
  'DR (Hardware Deployment Review)',
  'DP (Deployment Provisioning)',
  'PR (Package and Packaging Review)',
  'Outbound Shipping / Storage',
  'On-site Unboxing',
  'On-site System Installation',
  'On-Site Repair',
  'Base Arrival Review',
  'Base-MRU',
]);

/** Base string statuses that count as "grounded" for SystemStatus derivation. */
export const GROUNDED_BASE_STATUSES = new Set([]);

/** Base string statuses that count as "offline" for SystemStatus derivation. */
export const OFFLINE_BASE_STATUSES = new Set(['Retired']);

/** Base string statuses that count as "planned" for SystemStatus derivation. */
export const PLANNED_BASE_STATUSES = new Set(['Planned Deployment']);

/** Drone string statuses that count as "operational" for SystemStatus derivation. */
export const OPERATIONAL_DRONE_STATUSES = new Set([
  'Deployed Operational',
  'Deployed Backup',
  'Operational but Needs Maintenance',
  'Dev-Temporarily',
  'Dev-Permanently',
]);

/** Drone string statuses that count as "maintenance" for SystemStatus derivation. */
export const MAINTENANCE_DRONE_STATUSES = new Set([
  'No Status',
  'no-status',
  'In-bound Shipping',
  'HQ Arrival Review',
  'HW Build',
  'HW MRU',
  'SW MRU',
  'VP (validation provisioning)',
  'HW-VR (validation hw review)',
  'VHF (validation hand flights)',
  'VMF (validation mission flights)',
  'DP (deployment provision)',
  'HW-DR',
  'HW-PR',
  'Out-bound Shipping',
  'DCR (customer site arrival checks)',
]);

/** Drone string statuses that count as "grounded" for SystemStatus derivation. */
export const GROUNDED_DRONE_STATUSES = new Set([
  'Grounded (Pending MRU)',
  'Grounded (Pending Triage / Incident Investigation)',
  'Grounded (Pending Field MRU)',
]);

/** Drone string statuses that count as "offline" for SystemStatus derivation. */
export const OFFLINE_DRONE_STATUSES = new Set(['Retired']);

/* ---------------------- Normalized Shapes ---------------------- */

export type DroneModel = 'blockOneFive' | 'blockFive' | 'blockFiveT' | string;

export interface NormalizedMissionManagerUrl {
  id: string | null;
  name: string | null;
  url: string;
}

export interface NormalizedDrone {
  id: string;
  name: string;
  remoteIDnumber: string | null;
  faaRegistrationNumber: string | null;
  model: DroneModel | null;

  // status
  status: string | null;
  isGrounded: boolean | null;
  isOfflineDueToReportingTimeout: boolean; // true when stopReportToFmForMoreThan30Mins is true

  // battery
  batteryLevel: number | null; // 0–100 percentage
  chargingCycles: number | null;
  charging: boolean | null;

  // stats
  totalFlightMinutes: number | null;
  firstActivated: string | null;
}

export interface NormalizedBase {
  // identity
  id: string;
  name: string;

  // address
  fullAddress: string | null;
  partialAddress: string | null;

  // position
  lat: number | null;
  lon: number | null;

  // status
  systemStatus: SystemStatus; // derived from base + drone statuses
  baseStatus: string | null; // the base's own string status (e.g. "Customer Operation")
  chargingStatus: string | null;
  isOfflineDueToReportingTimeout: boolean; // true when stopReportToFmForMoreThan30Mins is true

  // mission stats
  totalMissions: number | null;
  successRate: number | null;
  averageMissionDuration: number | null; // minutes
  longestMissionDuration: number | null; // minutes
  shortestMissionDuration: number | null; // minutes
  numberOfNightMissions: number | null;
  numberOfDayMissions: number | null;
  lastMissionLandingDate: string | null;
  totalFlightMinutesBase: number | null; // minutes

  // alerts and scheduling
  totalNumberOfRaidEvents: number | null;
  weeklyScheduledMissions: number | null;

  // mission manager links
  missionManagerUrls: NormalizedMissionManagerUrl[] | null;

  // drones (dev drones filtered out)
  drones: NormalizedDrone[] | null;
}

export interface NormalizedApiResponse {
  bases: NormalizedBase[];
  startDate: string | null;
  endDate: string | null;
}

/* ---------------------- Misc UI Types ---------------------- */

export enum SortKey {
  LOCATION_NAME = 'locationName',
  BASE_NAME = 'baseName',
  MOST_MISSIONS = 'mostMissions',
  AVERAGE_MISSION_DURATION = 'averageMissionDuration',
  NUMBER_OF_NIGHT_MISSIONS = 'nightOps',
  NUMBER_OF_DAY_MISSIONS = 'dayOps',
}

export interface SystemStatusColors {
  core: string;
  background: string;
  bright: string;
  outline?: string;
  markerColor?: string; // normally, core is used for the background color of the marker, but for certain statuses (e.g. PLANNED) we use a different color for better visibility against the map
  markerTextColor?: string; // for the text / icon color on the marker, it's black by default
}
